import React from 'react';
import { Card, Badge, Form, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faExclamationCircle, faFilter, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import FalconCardHeader from '../../../../components/common/FalconCardHeader';

export default function ResumenVehiculos({ 
  resumen, 
  onVehiculoSelect, 
  vehiculoSeleccionado,
  vehiculosSeleccionados = [], // Nueva prop para múltiples vehículos
  filtroEstado,
  onFiltroEstadoChange
}) {
  
  // Función para crear tooltips explicativos
  const createTooltip = (text, id = Math.random().toString(36).substr(2, 9)) => (
    <Tooltip id={`tooltip-${id}`} className="fs-11">
      {text}
    </Tooltip>
  );

  // Opciones de filtro
  const filtroOpciones = [
    { 
      value: 'todos', 
      label: 'Todos los vehículos', 
      icon: null,
      tooltip: 'Mostrar todos los vehículos sin filtrar'
    },
    { 
      value: 'criticas', 
      label: 'Solo críticas', 
      icon: faExclamationTriangle,
      iconColor: 'text-danger',
      tooltip: 'Vehículos con inconsistencias críticas: diferencia mayor a 1 evento o secuencias incorrectas (dobles apagados/encendidos)'
    },
    { 
      value: 'menores', 
      label: 'Solo diferencias menores', 
      icon: faExclamationCircle,
      iconColor: 'text-warning',
      tooltip: 'Vehículos con diferencia exacta de 1 evento entre apagados y encendidos'
    },
    { 
      value: 'saludables', 
      label: 'Sin problemas', 
      icon: faCheckCircle,
      iconColor: 'text-success',
      tooltip: 'Vehículos sin problemas: misma cantidad de apagados y encendidos'
    },
    { 
      value: 'problemas', 
      label: 'Todos los problemas', 
      icon: faFilter,
      iconColor: 'text-primary',
      tooltip: 'Mostrar solo vehículos con algún tipo de problema (críticas + diferencias menores)'
    }
  ];

  // Validar que resumen sea un array válido
  const resumenValido = Array.isArray(resumen) ? resumen : [];
  const cantidadVehiculos = resumenValido.length;

  const totalInconsistencias = resumenValido.filter(v => v.tieneInconsistencia).length;
  const totalDiferenciasMenures = resumenValido.filter(v => {
    const diferencia = Math.abs(v.totalApagados - v.totalEncendidos);
    return !v.tieneInconsistencia && diferencia === 1;
  }).length;
  const totalSinProblemas = resumenValido.filter(v => {
    const diferencia = Math.abs(v.totalApagados - v.totalEncendidos);
    return !v.tieneInconsistencia && diferencia === 0;
  }).length;
  
  const MAX_VEHICULOS_VISIBLES = 8;

  const calcularPorcentajeDiferencia = (apagados, encendidos) => {
    const total = apagados + encendidos;
    if (total === 0) return 0;
    const diferencia = Math.abs(apagados - encendidos);
    return ((diferencia / total) * 100).toFixed(1);
  };

  const style1Lines = {
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
  };

  // Determinar si necesitamos scroll
  const necesitaScroll = cantidadVehiculos > MAX_VEHICULOS_VISIBLES;
  const alturaTabla = necesitaScroll ? '320px' : 'auto';

  // Función para manejar click en checkbox específicamente
  const handleCheckboxClick = (vehiculo, event) => {
    // Evitar que el evento se propague a la fila
    event.stopPropagation();
    event.preventDefault();
    
    // 🔧 CORRECCIÓN: Usar la misma lógica que estaSeleccionado() para detectar si ya está seleccionado
    const yaSeleccionado = estaSeleccionado(vehiculo);
    
    if (yaSeleccionado) {
      // Si está seleccionado (ya sea en modo simple o múltiple), deseleccionarlo
      if (vehiculoSeleccionado?.uid_vehiculo === vehiculo.uid_vehiculo) {
        // Está en modo simple, deseleccionar completamente
        onVehiculoSelect(null, false);
      } else {
        // Está en modo múltiple, remover de la selección
        const nuevaSeleccion = vehiculosSeleccionados.filter(v => v.uid_vehiculo !== vehiculo.uid_vehiculo);
        onVehiculoSelect(nuevaSeleccion, true);
      }
    } else {
      // No está seleccionado, agregarlo
      let nuevaSeleccion = [...vehiculosSeleccionados];
      
      // Si hay selección simple y no está en múltiples, añadirla
      if (vehiculoSeleccionado && !nuevaSeleccion.some(v => v.uid_vehiculo === vehiculoSeleccionado.uid_vehiculo)) {
        nuevaSeleccion.push(vehiculoSeleccionado);
      }
      
      nuevaSeleccion.push(vehiculo);
      onVehiculoSelect(nuevaSeleccion, true);
    }
  };

  // Función para manejar click en la fila (sin checkbox)
  const handleRowClick = (vehiculo, event) => {
    // Solo procesar si no es click en checkbox o su contenedor
    if (event.target.type === 'checkbox' || 
        event.target.closest('.form-check') || 
        event.target.classList.contains('form-check-input')) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      // Selección múltiple con Ctrl/Cmd - convertir selección simple a múltiple si existe
      let nuevaSeleccion = [...vehiculosSeleccionados];
      
      // Si hay selección simple, añadirla a la múltiple
      if (vehiculoSeleccionado && !vehiculosSeleccionados.some(v => v.uid_vehiculo === vehiculoSeleccionado.uid_vehiculo)) {
        nuevaSeleccion.push(vehiculoSeleccionado);
      }
      
      const yaSeleccionado = nuevaSeleccion.some(v => v.uid_vehiculo === vehiculo.uid_vehiculo);
      if (yaSeleccionado) {
        // Remover de la selección
        nuevaSeleccion = nuevaSeleccion.filter(v => v.uid_vehiculo !== vehiculo.uid_vehiculo);
      } else {
        // Agregar a la selección
        nuevaSeleccion.push(vehiculo);
      }
      
      onVehiculoSelect(nuevaSeleccion, true);
    } else if (event.shiftKey && (vehiculosSeleccionados.length > 0 || vehiculoSeleccionado)) {
      // Selección por rango con Shift
      let referencia = vehiculosSeleccionados.length > 0 
        ? vehiculosSeleccionados[vehiculosSeleccionados.length - 1] 
        : vehiculoSeleccionado;
        
      const indiceReferencia = resumenValido.findIndex(v => v.uid_vehiculo === referencia.uid_vehiculo);
      const indiceActual = resumenValido.findIndex(v => v.uid_vehiculo === vehiculo.uid_vehiculo);
      
      if (indiceReferencia !== -1 && indiceActual !== -1) {
        const inicio = Math.min(indiceReferencia, indiceActual);
        const fin = Math.max(indiceReferencia, indiceActual);
        const vehiculosEnRango = resumenValido.slice(inicio, fin + 1);
        
        // Combinar con selección existente
        let nuevaSeleccion = [...vehiculosSeleccionados];
        
        // Si hay selección simple, añadirla
        if (vehiculoSeleccionado && !nuevaSeleccion.some(v => v.uid_vehiculo === vehiculoSeleccionado.uid_vehiculo)) {
          nuevaSeleccion.push(vehiculoSeleccionado);
        }
        
        vehiculosEnRango.forEach(v => {
          if (!nuevaSeleccion.some(sel => sel.uid_vehiculo === v.uid_vehiculo)) {
            nuevaSeleccion.push(v);
          }
        });
        
        onVehiculoSelect(nuevaSeleccion, true);
      }
    } else {
      // Selección simple
      // 🔧 Si ya está seleccionado en modo simple, deseleccionarlo
      if (vehiculoSeleccionado?.uid_vehiculo === vehiculo.uid_vehiculo) {
        onVehiculoSelect(null, false); // Deseleccionar
      } else {
        onVehiculoSelect(vehiculo, false); // Seleccionar
      }
    }
  };

  // Verificar si un vehículo está seleccionado
  const estaSeleccionado = (vehiculo) => {
    if (vehiculosSeleccionados.length > 0) {
      return vehiculosSeleccionados.some(v => v.uid_vehiculo === vehiculo.uid_vehiculo);
    }
    return vehiculoSeleccionado?.uid_vehiculo === vehiculo.uid_vehiculo;
  };

  return (
    <Card className="h-100 card-deck mt-3">
      <FalconCardHeader
        title={
          <div className="d-flex align-items-center flex-wrap">
            <span>Resumen por Vehículo</span>
            <Badge bg="secondary" className="ms-2">
              {cantidadVehiculos} vehículos
            </Badge>
            {vehiculosSeleccionados.length > 0 && (
              <Badge bg="primary" className="ms-2">
                {vehiculosSeleccionados.length} seleccionados
              </Badge>
            )}
            {totalInconsistencias > 0 && (
              <OverlayTrigger
                placement="top"
                overlay={createTooltip('Inconsistencias críticas: diferencia mayor a 1 evento o secuencias incorrectas (dobles apagados/encendidos consecutivos)', 'criticas')}
              >
                <Badge bg="danger" className="ms-2" style={{ cursor: 'help' }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                  {totalInconsistencias} inconsistencias críticas
                </Badge>
              </OverlayTrigger>
            )}
            {totalDiferenciasMenures > 0 && (
              <OverlayTrigger
                placement="top"
                overlay={createTooltip('Diferencias menores: exactamente 1 evento de diferencia entre apagados y encendidos', 'menores')}
              >
                <Badge bg="warning" className="ms-2" style={{ cursor: 'help' }}>
                  <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
                  {totalDiferenciasMenures} diferencias menores
                </Badge>
              </OverlayTrigger>
            )}
            {totalSinProblemas > 0 && (
              <OverlayTrigger
                placement="top"
                overlay={createTooltip('Sin problemas: misma cantidad de eventos de apagado y encendido (diferencia = 0)', 'sinproblemas')}
              >
                <Badge bg="success" className="ms-2" style={{ cursor: 'help' }}>
                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                  {totalSinProblemas} sin problemas
                </Badge>
              </OverlayTrigger>
            )}
          </div>
        }
        titleTag="h6"
        light
        endEl={
          <div className="d-flex align-items-center gap-2">
            <Form.Select 
              size="sm" 
              value={filtroEstado}
              onChange={(e) => onFiltroEstadoChange(e.target.value)}
              style={{ width: '200px' }}
              className="fs-11"
            >
              {filtroOpciones.map((opcion) => (
                <option key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </option>
              ))}
            </Form.Select>
            <OverlayTrigger
              placement="top"
              overlay={createTooltip('Filtrar vehículos por estado: críticas, diferencias menores o sin problemas', 'filtro-info')}
            >
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="text-muted" 
                style={{ cursor: 'help' }}
              />
            </OverlayTrigger>
            {/* {vehiculosSeleccionados.length === 0 && (
              <small className="text-muted fs-11 ms-2">
                <strong>💡 Tip:</strong> <kbd>Ctrl+Click</kbd> para múltiples o <kbd>Shift+Click</kbd> para rango
              </small>
            )} */}
          </div>
        }
      />

      <Card.Body className="p-0">
        <div 
          style={{ 
            maxHeight: alturaTabla, 
            overflowY: necesitaScroll ? 'auto' : 'visible',
            overflowX: 'hidden'
          }}
        >
          <table className='fs-10 mb-0 overflow-hidden table table-striped'>
            <thead className="table-light sticky-top" style={{ zIndex: 1 }}>
              <tr>
                <th className="text-900 text-center" style={{width: '5%'}}>Estado</th>
                <th className="text-900 text-center" style={{width: '8%'}}>
                  <div onClick={(e) => {
                    e.stopPropagation();
                    if (vehiculosSeleccionados.length > 0 && vehiculosSeleccionados.length === resumenValido.length) {
                      // Deseleccionar todos
                      onVehiculoSelect([], true);
                    } else {
                      // Seleccionar todos
                      onVehiculoSelect(resumenValido, true);
                    }
                  }}>
                    <Form.Check
                      type="checkbox"
                      id="select-all"
                      checked={vehiculosSeleccionados.length > 0 && vehiculosSeleccionados.length === resumenValido.length}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = vehiculosSeleccionados.length > 0 && vehiculosSeleccionados.length < resumenValido.length;
                        }
                      }}
                      onChange={() => {}} // Manejado por el div contenedor
                      className="fs-11"
                      style={{ pointerEvents: 'none' }}
                    />
                  </div>
                </th>
                <th className="text-900" style={{width: '30%'}}>Vehículo</th>
                <th className="text-900 text-center" style={{width: '12%'}}>Apagados</th>
                <th className="text-900 text-center" style={{width: '12%'}}>Encendidos</th>
                <th className="text-900 text-center" style={{width: '12%'}}>Diferencia</th>
                <th className="text-900 text-center" style={{width: '21%'}}>% Diferencia</th>
              </tr>
            </thead>
            <tbody>
              {resumenValido.map((vehiculo) => {
                const diferencia = Math.abs(vehiculo.totalApagados - vehiculo.totalEncendidos);
                const porcentajeDiferencia = calcularPorcentajeDiferencia(
                  vehiculo.totalApagados, 
                  vehiculo.totalEncendidos
                );
                
                return (
                  <tr 
                    key={vehiculo.uid_vehiculo}
                    className={classNames('cursor-pointer', {
                      'table-active bg-primary-subtle': estaSeleccionado(vehiculo)
                    })}
                    onClick={(event) => handleRowClick(vehiculo, event)}
                    style={{ 
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!estaSeleccionado(vehiculo)) {
                        e.currentTarget.style.backgroundColor = '#e3f2fd';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!estaSeleccionado(vehiculo)) {
                        e.currentTarget.style.backgroundColor = '';
                        e.currentTarget.style.boxShadow = '';
                        e.currentTarget.style.transform = '';
                      }
                    }}
                  >
                    <td className="text-center">
                      {vehiculo.tieneInconsistencia ? (
                        <FontAwesomeIcon 
                          icon={faExclamationTriangle} 
                          className="text-danger" 
                          title="Inconsistencia crítica detectada"
                        />
                      ) : diferencia === 1 ? (
                        <FontAwesomeIcon 
                          icon={faExclamationCircle} 
                          className="text-warning" 
                          title="Diferencia menor detectada"
                        />
                      ) : (
                        <FontAwesomeIcon 
                          icon={faCheckCircle} 
                          className="text-success" 
                          title="Sin problemas"
                        />
                      )}
                    </td>
                    
                    <td className="text-center">
                      <div onClick={(e) => {
                        // Evitar propagación desde el contenedor del checkbox
                        e.stopPropagation();
                        handleCheckboxClick(vehiculo, e);
                      }}>
                        <Form.Check
                          type="checkbox"
                          id={`select-${vehiculo.uid_vehiculo}`}
                          checked={estaSeleccionado(vehiculo)}
                          onChange={() => {}} // Manejado por el div contenedor
                          className="fs-11"
                          style={{ pointerEvents: 'none' }} // Deshabilitar eventos directos del checkbox
                        />
                      </div>
                    </td>
                    
                    <td>
                      <Row className="align-items-center g-0">
                        <Col md={12}>
                          <div className="fs-10 fw-bold">
                            <span
                              style={{ ...style1Lines, minWidth: '80px' }}
                              title={vehiculo.label}
                            >
                              {vehiculo.label}
                            </span>
                          </div>
                          {vehiculo.tieneInconsistencia && vehiculo.tipoInconsistencia && vehiculo.tipoInconsistencia.length > 0 && (
                            <div className="mt-1">
                              {vehiculo.tipoInconsistencia.slice(0, 2).map((tipo, index) => (
                                <Badge key={index} bg="danger" className="me-1 fs-11" style={{fontSize: '0.65em'}}>
                                  {tipo}
                                </Badge>
                              ))}
                              {vehiculo.tipoInconsistencia.length > 2 && (
                                <Badge bg="outline-secondary" className="fs-11" style={{fontSize: '0.65em'}}>
                                  {/* +{vehiculo.tipoInconsistencia.length - 2} */}
                                </Badge>
                              )}
                            </div>
                          )}
                          {!vehiculo.tieneInconsistencia && diferencia === 1 && (
                            <div className="mt-1">
                              <Badge bg="warning" className="fs-11" style={{fontSize: '0.65em'}}>
                                Diferencia menor: {diferencia} evento
                              </Badge>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </td>
                    
                    <td className="text-center">
                      <Row className="justify-content-center align-items-center g-0">
                        <Col xs="auto">
                          <div className="fs-10 fw-bold" style={{ color: '#dc3545' }}>
                            {vehiculo.totalApagados}
                          </div>
                        </Col>
                      </Row>
                    </td>
                    
                    <td className="text-center">
                      <Row className="justify-content-center align-items-center g-0">
                        <Col xs="auto">
                          <div className="fs-10 fw-bold" style={{ color: '#28a745' }}>
                            {vehiculo.totalEncendidos}
                          </div>
                        </Col>
                      </Row>
                    </td>
                    
                    <td className="text-center">
                      <Row className="justify-content-center align-items-center g-0">
                        <Col xs="auto">
                          <div className={classNames('fs-10 fw-bold', {
                            'text-danger': diferencia > 1,
                            'text-warning': diferencia === 1,
                            'text-success': diferencia === 0
                          })}>
                            {diferencia}
                          </div>
                        </Col>
                      </Row>
                    </td>
                    
                    <td className="text-center">
                      <Row className="justify-content-center align-items-center g-0">
                        <Col xs="auto">
                          <Badge 
                            pill 
                            bg={porcentajeDiferencia > 20 ? "danger" : porcentajeDiferencia > 10 ? "warning" : "success"}
                            text="dark"
                            className="fw-bold fs-10"
                            style={{ minWidth: '45px' }}
                          >
                            {porcentajeDiferencia}%
                          </Badge>
                        </Col>
                      </Row>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {cantidadVehiculos === 0 && (
          <div className="text-center py-4 text-muted">
            <p className="mb-0 fs-10 fw-semibold">No hay vehículos para mostrar</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
